import { baseApi as api } from 'shared/api/baseApi';
import { getCookie } from 'shared/utils';
const injectedRtkApi = api.injectEndpoints({
	endpoints: (build) => ({
		plannerPostsRetrieve: build.query<PlannerPostsRetrieveApiResponse, PlannerPostsRetrieveApiArg>({
			query: (queryArg) => ({
				url: `/api/planner/posts/`,
				params: {
					channel_ids: queryArg.channelIds,
					published_at_from: queryArg.publishedAtFrom,
					published_at_to: queryArg.publishedAtTo,
					search: queryArg.search,
				},
			}),
		}),
		plannerPostsCreate: build.mutation<PlannerPostsCreateApiResponse, FormData>({
			query: (queryArg) => ({
				url: `/api/planner/posts/`,
				method: 'POST',
				body: queryArg,
				headers: { 'X-Csrftoken': getCookie('csrftoken') },
				formData: true,
			}),
		}),
		plannerPostsPartialUpdate: build.mutation<PlannerPostsPartialUpdateApiResponse, PlannerPostsPartialUpdateApiArg>({
			query: (queryArg) => ({
				url: `/api/planner/posts/${queryArg.plannedPostId}/`,
				method: 'PATCH',
				body: queryArg.patchedUpdatePlannerPost,
				headers: { 'X-Csrftoken': getCookie('csrftoken') },
				formData: true,
			}),
		}),
		plannerPostsDestroy: build.mutation<PlannerPostsDestroyApiResponse, PlannerPostsDestroyApiArg>({
			query: (queryArg) => ({
				url: `/api/planner/posts/${queryArg}/`,
				method: 'DELETE',
				headers: { 'X-Csrftoken': getCookie('csrftoken') },
			}),
		}),
		plannerPostsPublicationsList: build.query<
			PlannerPostsPublicationsListApiResponse,
			PlannerPostsPublicationsListApiArg
		>({
			query: (queryArg) => ({
				url: `/api/planner/posts/${queryArg.plannedPostId}/publications/`,
				params: {
					limit: queryArg.limit,
					offset: queryArg.offset,
				},
			}),
		}),
		plannerPostsImagesList: build.query<PlannerPostsImagesListApiResponse, PlannerPostsImagesListApiArg>({
			query: (queryArg) => ({
				url: `/api/planner/posts_images/`,
				params: {
					limit: queryArg.limit,
					offset: queryArg.offset,
					planned_post_id: queryArg.plannedPostId,
				},
			}),
		}),
		plannerPostsImagesCreate: build.mutation<PlannerPostsImagesCreateApiResponse, PlannerPostsImagesCreateApiArg>({
			query: (queryArg) => ({
				url: `/api/planner/posts_images/`,
				method: 'POST',
				body: queryArg,
				headers: { 'X-Csrftoken': getCookie('csrftoken') },
			}),
		}),
		plannerPostsImagesRetrieve: build.query<PlannerPostsImagesRetrieveApiResponse, PlannerPostsImagesRetrieveApiArg>({
			query: (queryArg) => ({ url: `/api/planner/posts_images/${queryArg}/` }),
		}),
		plannerPostsImagesDestroy: build.mutation<PlannerPostsImagesDestroyApiResponse, PlannerPostsImagesDestroyApiArg>({
			query: (queryArg) => ({
				url: `/api/planner/posts_images/${queryArg}/`,
				method: 'DELETE',
				headers: { 'X-Csrftoken': getCookie('csrftoken') },
			}),
		}),
	}),
	overrideExisting: false,
});
export { injectedRtkApi as enhancedApi };
export type PlannerPostsRetrieveApiResponse = /** status 200  */ PlannerPostResponse;
export type PlannerPostsRetrieveApiArg = {
	/** Список ID каналов для фильтрации */
	channelIds?: string[];
	/** Начальная дата публикации (включительно) */
	publishedAtFrom?: string;
	/** Конечная дата публикации (включительно) */
	publishedAtTo?: string;
	/** Поисковый запрос */
	search?: string;
};
export type PlannerPostsCreateApiResponse = /** status 201  */ PlannerPost;
export type PlannerPostsCreateApiArg = CreatePlannerPost;
export type PlannerPostsPartialUpdateApiResponse = /** status 200  */ PlannerPost;
export type PlannerPostsPartialUpdateApiArg = {
	plannedPostId: string;
	patchedUpdatePlannerPost: FormData;
};
export type PlannerPostsDestroyApiResponse = unknown;
export type PlannerPostsDestroyApiArg = string;
export type PlannerPostsPublicationsListApiResponse = /** status 200  */ ChannelPublicationInfo[];
export type PlannerPostsPublicationsListApiArg = {
	/** Number of results to return per page. */
	limit?: number;
	/** The initial index from which to return the results. */
	offset?: number;
	plannedPostId: string;
};
export type PlannerPostsImagesListApiResponse = /** status 200  */ PaginatedPlannedPostImageList;
export type PlannerPostsImagesListApiArg = {
	/** Number of results to return per page. */
	limit?: number;
	/** The initial index from which to return the results. */
	offset?: number;
	plannedPostId?: string;
};
export type PlannerPostsImagesCreateApiResponse = /** status 201  */ PlannedPostImage;
export type PlannerPostsImagesCreateApiArg = PlannedPostImage;
export type PlannerPostsImagesRetrieveApiResponse = /** status 200  */ PlannedPostImage;
export type PlannerPostsImagesRetrieveApiArg = /** A UUID string identifying this planned post image. */ string;
export type PlannerPostsImagesDestroyApiResponse = unknown;
export type PlannerPostsImagesDestroyApiArg = /** A UUID string identifying this planned post image. */ string;
export type PlannerPostOffer = {
	id: string;
	name: string;
	image: string;
};
export type PlannerPostBrand = {
	id: string;
	name: string;
	image: string;
};
export type PlannerPostChannel = {
	id: string;
	name: string;
	logo_file_path: string;
	affiliate_link: string;
	published_at: string;
	views_count: number;
	likes_count: number;
	comments_count: number;
};
export type PlannerPost = {
	id: string;
	published_at: string;
	offer: PlannerPostOffer;
	brand: PlannerPostBrand;
	channels: PlannerPostChannel[];
	images: string[];
	content: string;
	auto_deletion_after: AutoDeletionAfterEnum;
};
export type PlannerPostResponse = {
	results: PlannerPost[];
};
export type CreatePlannerPost = {
	post_id: string;
	channel_ids: string[];
	published_at?: string | null;
	auto_deletion_after?: AutoDeletionAfterEnum;
	content?: string | null;
	images?: string[] | null;
};
export type PatchedUpdatePlannerPost = {
	channel_ids?: string[] | null;
	published_at?: string;
	auto_deletion_after?: AutoDeletionAfterEnum;
	content?: string | null;
	images?: string[] | null;
};
export type PublishedPostInfo = {
	post_link: string;
	published_at: string;
	views_count: number;
	likes_count: number;
	comments_count: number;
};
export type ChannelPublicationInfo = {
	channel_name: string;
	channel_link: string;
	published_post: PublishedPostInfo | null;
};
export type PlannedPostImage = {
	uuid?: string;
	planned_post_id: string;
	image: string;
	position?: number;
};
export type PaginatedPlannedPostImageList = {
	count: number;
	next?: string | null;
	previous?: string | null;
	results: PlannedPostImage[];
};
export enum AutoDeletionAfterEnum {
	Never = 'NEVER',
	OneDay = 'one_day',
	TwoDays = 'two_days',
}
